'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { Box, Stack, SxProps, Theme, Typography } from '@mui/material';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import {
  formatNotificationTime,
  getCommunityHeadline,
  getCommunityPostPath,
  getInitials,
  type CommunityNotificationLike
} from './communityNotifications';

/**
 * SB-1912 — Community colours and type from the approved Figma
 * (Final Design → Notification, node 4658:27405).
 */
export const COMMUNITY_TOKENS = {
  amberText: '#A16807',
  amberFill: '#FEF1DB',
  amberBorder: '#FCE0B1',
  amberDot: '#F59E0B',
  bundleBg: 'rgba(254, 248, 238, 0.5)',
  unreadBg: 'rgba(237, 242, 253, 0.5)',
  unreadDot: '#004AE0',
  link: '#004AE0',
  title: '#262A34',
  subtitle: '#4E576B',
  body: '#626E88',
  time: '#7684A3'
} as const;

type CommunityNotificationCardProps = {
  notification: CommunityNotificationLike & {
    _id: string;
    message: string;
    isRead: boolean;
    createdAt: string;
    image_url?: string;
  };
  /** Marks it read and closes the panel; navigation is the link's job. */
  onSelect: (id: string, isRead: boolean) => void;
};

/**
 * A comment on your post, a reply to your comment, or a mention. The whole
 * card opens the post at its replies.
 */
const CommunityNotificationCard: React.FC<CommunityNotificationCardProps> = ({
  notification,
  onSelect
}) => {
  const headline = getCommunityHeadline(notification);
  const href = getCommunityPostPath(notification);
  const isUnread = !notification.isRead;

  const body = (
    <Box sx={styles.card(isUnread)}>
      {isUnread && <Box sx={styles.dot(COMMUNITY_TOKENS.unreadDot)} />}
      <CommunityAvatar
        name={notification.actor?.full_name}
        imageUrl={
          notification.actor?.profile_image_url || notification.image_url
        }
      />

      <Stack spacing={0.5} flex={1} minWidth={0}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <CommunityChip label="Community" />
          <Typography sx={styles.time}>
            {formatNotificationTime(notification.createdAt)}
          </Typography>
        </Stack>

        <Typography component="h3" sx={styles.headline}>
          {headline}
        </Typography>
        <Typography sx={styles.subtitle}>ScholarBee Community</Typography>
        <Typography sx={styles.message}>{notification.message}</Typography>

        {href && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={styles.cta}
            aria-hidden
          >
            <span>View conversation</span>
            <span>→</span>
          </Stack>
        )}
      </Stack>
    </Box>
  );

  if (!href) {
    return (
      <Box
        onClick={() => onSelect(notification._id, notification.isRead)}
        sx={{ cursor: 'pointer' }}
      >
        {body}
      </Box>
    );
  }

  return (
    <Link
      href={href}
      aria-label={`${headline}. View conversation`}
      onClick={() => onSelect(notification._id, notification.isRead)}
      style={{ textDecoration: 'none', display: 'block' }}
    >
      {body}
    </Link>
  );
};

/** The actor's photo, or their initials on the design's amber tile. */
export const CommunityAvatar: React.FC<{
  name?: string;
  imageUrl?: string;
  label?: string;
}> = ({ name, imageUrl, label }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = imageUrl && !imageFailed;

  return (
    <Box sx={styles.avatar} aria-hidden>
      {showImage ? (
        <Box
          component="img"
          src={imageUrl}
          alt=""
          onError={() => setImageFailed(true)}
          sx={styles.avatarImage}
        />
      ) : (
        <Typography sx={styles.avatarText}>
          {label ?? getInitials(name)}
        </Typography>
      )}
    </Box>
  );
};

export const CommunityChip: React.FC<{ label: string }> = ({ label }) => (
  <Box sx={styles.chip}>
    <PeopleAltOutlinedIcon sx={{ fontSize: 14 }} />
    <span>{label}</span>
  </Box>
);

const styles = {
  card: (isUnread: boolean): SxProps<Theme> => ({
    position: 'relative',
    display: 'flex',
    gap: '12px',
    padding: '16px 20px',
    backgroundColor: isUnread ? COMMUNITY_TOKENS.unreadBg : '#FFFFFF',
    transition: 'background-color 0.2s',
    '&:hover': { backgroundColor: COMMUNITY_TOKENS.unreadBg }
  }),
  dot: (color: string): SxProps<Theme> => ({
    position: 'absolute',
    left: 6,
    top: 36,
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: color
  }),
  avatar: {
    width: 48,
    height: 48,
    flexShrink: 0,
    borderRadius: '14px',
    border: `1px solid ${COMMUNITY_TOKENS.amberBorder}`,
    backgroundColor: COMMUNITY_TOKENS.amberFill,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden'
  },
  avatarImage: { width: '100%', height: '100%', objectFit: 'cover' },
  avatarText: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 700,
    fontSize: '14px',
    lineHeight: '20px',
    color: COMMUNITY_TOKENS.amberText
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    height: '24px',
    px: '10px',
    borderRadius: '40px',
    backgroundColor: COMMUNITY_TOKENS.amberFill,
    color: COMMUNITY_TOKENS.amberText,
    fontFamily: 'Inter, sans-serif',
    fontWeight: 600,
    fontSize: '12px',
    lineHeight: '16px',
    whiteSpace: 'nowrap',
    flexShrink: 0
  },
  time: {
    ml: 'auto !important',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '16px',
    color: COMMUNITY_TOKENS.time,
    whiteSpace: 'nowrap'
  },
  headline: {
    pt: '4px',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 700,
    fontSize: '16px',
    lineHeight: '24px',
    letterSpacing: '-0.18px',
    color: COMMUNITY_TOKENS.title
  },
  subtitle: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    color: COMMUNITY_TOKENS.subtitle
  },
  message: {
    pt: '4px',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 400,
    fontSize: '12px',
    lineHeight: '16px',
    color: COMMUNITY_TOKENS.body,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden'
  },
  cta: {
    pt: '8px',
    fontFamily: 'Inter, sans-serif',
    fontSize: '14px',
    lineHeight: '24px',
    color: COMMUNITY_TOKENS.link
  }
} as const;

export default CommunityNotificationCard;
